import { CardContent, Divider, Grid2 as Grid, Typography } from '@mui/material'
import Paper from 'components/@extended/Paper'
import ComponentDisplay from 'components/ComponentDisplay'
import type { ExpandedCompensationPlan } from 'types/comp-plan'
import type { Component, TargetDTO } from 'types/components'
import { getCompositeFunction } from 'types/components'
import type { Period } from 'types/periods'
import { createIdLabelMap } from 'types/periods'

interface PlanTargetRowProps {
  componentId: string
  component?: Component
  target?: TargetDTO
  reviewOnly?: boolean
  periodLabel?: string
}

const PlanTargetRow: React.FC<PlanTargetRowProps> = ({ componentId, component, target, periodLabel }) => {
  if (component == null || target == null) {
    return <></>
  }

  return (
    <>
      {/* Desktop Layout - Hidden on mobile */}
      <Grid container spacing={2} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
        <Grid size={{ xs: 4 }}>
          <Typography variant="h6">{component.label}</Typography>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Typography align="center">{periodLabel ?? ''}</Typography>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Typography align="center">
            <ComponentDisplay
              componentType={component.componentType}
              value={target.value || '0'}
              sourceType={component.sourceType}
              compositeFunction={getCompositeFunction(component.sourceInfo)}
              componentLabel={component.label}
              periodLabel={periodLabel}
              isTarget={true}
              fontSize="inherit"
            />
          </Typography>
        </Grid>
      </Grid>

      {/* Mobile Layout - Stacked */}
      <Grid container spacing={1} sx={{ display: { xs: 'flex', md: 'none' } }}>
        {/* Target Name */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            {component.label}
          </Typography>
        </Grid>

        {/* Period and Value in 2-column layout */}
        <Grid size={{ xs: 6 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Period
          </Typography>
          <Typography variant="body2">{periodLabel ?? ''}</Typography>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Value
          </Typography>
          <Typography variant="body2">
            <ComponentDisplay
              componentType={component.componentType}
              value={target.value || '0'}
              sourceType={component.sourceType}
              compositeFunction={getCompositeFunction(component.sourceInfo)}
              componentLabel={component.label}
              periodLabel={periodLabel}
              isTarget={true}
              fontSize="inherit"
            />
          </Typography>
        </Grid>
      </Grid>
    </>
  )
}

interface PlanTargetsProps {
  plan: ExpandedCompensationPlan
  period: Period
}

const PlanTargetsCard: React.FC<PlanTargetsProps> = ({ plan, period }) => {
  if (plan == null) {
    return <></>
  }

  const componentsMap = plan.components.reduce((acc: Record<string, Component>, component) => {
    if (component.id != null) {
      acc[component.id] = component
    }
    return acc
  }, {})

  // Updated to use a composite key of componentId and periodId
  const targetsComponentIdMap = plan.targets.reduce((acc: Record<string, TargetDTO>, target) => {
    const key = `${target.componentId}_${target.periodId}`
    acc[key] = target
    return acc
  }, {})

  const activeTargetComponentIds = plan.plan.variableAllocations.reduce((acc: Set<string>, table) => {
    const activeTargetsForTable = table.rows.reduce((rowAcc: Set<string>, row) => {
      if (!(row.disabled ?? false)) {
        ;(row.componentIds ?? []).forEach((id) => {
          const periodId = row.periodId // Assuming periodId is available in row
          const compositeKey = `${id}_${periodId}`
          rowAcc.add(compositeKey)
        })
      }
      return rowAcc
    }, new Set<string>())

    activeTargetsForTable.forEach((id) => acc.add(id))
    return acc
  }, new Set<string>())
  // Convert Set back to array if needed elsewhere as array
  const uniqueActiveTargetComponentIds = Array.from(activeTargetComponentIds)

  const periodIdLabelMap = createIdLabelMap(period)

  return (
    <div style={{ width: '100%' }}>
      <Paper sx={{ paddingTop: 1, width: '100%' }}>
        <CardContent sx={{ px: { xs: 1, sm: 2 }, py: { xs: 2, sm: 3 } }}>
          {/* Table Title */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                Targets
              </Typography>
            </Grid>
          </Grid>

          {/* Desktop Table Header - Hidden on mobile */}
          <Grid container spacing={2} sx={{ display: { xs: 'none', md: 'flex' }, mb: 2 }}>
            <Grid size={{ xs: 4 }}>
              <Typography variant="h5">Target</Typography>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Typography variant="h5" align="center">
                Period
              </Typography>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Typography variant="h5" align="center">
                Value
              </Typography>
            </Grid>
          </Grid>

          {uniqueActiveTargetComponentIds.map((compositeId, index) => {
            const [componentId, periodId] = compositeId.split('_')
            const component = componentsMap[componentId]
            const target = targetsComponentIdMap[compositeId]
            const periodLabel = periodIdLabelMap[periodId]

            if (component === undefined || target === undefined) {
              return <></>
            }

            return (
              <div key={compositeId}>
                {index > 0 && (
                  <div style={{ marginTop: 10, marginBottom: 10 }}>
                    <Divider />
                  </div>
                )}

                <div style={{ marginBottom: 16 }}>
                  <PlanTargetRow
                    componentId={componentId}
                    component={component}
                    target={target}
                    periodLabel={periodLabel}
                  />
                </div>
              </div>
            )
          })}
        </CardContent>
      </Paper>
    </div>
  )
}

export default PlanTargetsCard
